import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useMember } from '../context/MemberContext';
import { Menu, X, Shield, User, LogOut } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9645c0b6-481f-4d07-81fb-c2ea43b51b14/artifacts/rw309x94_VoidWolves2-Photoroom.png";

const navLinks = [
    { path: '/', label: 'The Bridge' },
    { path: '/hangar', label: 'Hangar' },
    { path: '/personnel', label: 'Personnel' },
    { path: '/war-room', label: 'War Room' },
    { path: '/announcements', label: 'Announcements' },
    { path: '/operations', label: 'Operations' },
];

export const Navigation = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();
    const { admin, logout: adminLogout, loginWithToken } = useAuth();
    const { member, logout: memberLogout, getAuthHeaders: memberHeaders } = useMember();
    const navigate = useNavigate();

    const backToUser = () => {
        navigate('/operations');
        adminLogout();
        toast.success(`Switched back to ${member.callsign}`);
    };

    const switchToAdmin = async () => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/member/auth/switch-to-admin`, {}, memberHeaders());
            await loginWithToken(res.data.access_token);
            toast.success('Switched to admin account');
            navigate('/admin');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to switch account');
        }
    };

    return (
        <nav className="fixed w-full z-50 bg-black/80 backdrop-blur-md border-b border-white/10" data-testid="main-navigation">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between gap-4 h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
                        <img 
                            src={LOGO_URL} 
                            alt="Voidwolves" 
                            className="h-10 w-auto hud-glow"
                        />
                        <div className="hidden sm:block">
                            <span className="font-heading font-black text-white text-lg tracking-tight">VOIDWOLVES</span>
                            <span className="font-mono text-[10px] text-gray-500 block tracking-[0.2em]">126TH CARRIER STRIKE GROUP</span>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-1">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                {link.label}
                            </Link>
                        ))}
                        
                        {admin && (
                            <Link
                                to="/admin"
                                className={`nav-link flex items-center gap-1 ${location.pathname === '/admin' ? 'active' : ''}`}
                                data-testid="nav-admin"
                            >
                                <Shield className="w-3 h-3" />
                                Admin
                            </Link>
                        )}
                    </div>

                    {/* Auth Buttons */}
                    <div className="hidden lg:flex items-center gap-4 shrink-0">
                        {/* Member display */}
                        {member && !admin && (
                            <div className="flex items-center gap-3">
                                <span className="font-mono text-xs text-green-400">
                                    <User className="w-3 h-3 inline mr-1" />
                                    {member.callsign}
                                </span>
                                {member.has_linked_admin && (
                                    <button
                                        onClick={switchToAdmin}
                                        className="btn-primary text-xs py-2 px-4"
                                        data-testid="switch-to-admin-button"
                                    >
                                        <Shield className="w-3 h-3 inline mr-1" />
                                        Switch To Admin
                                    </button>
                                )}
                                <button
                                    onClick={memberLogout}
                                    className="btn-secondary text-xs py-2 px-4"
                                    data-testid="member-logout-button"
                                >
                                    <LogOut className="w-3 h-3 inline mr-1" />
                                    Logout
                                </button>
                            </div>
                        )}
                        
                        {/* Admin display */}
                        {admin ? (
                            <div className="flex items-center gap-3">
                                <span className="font-mono text-xs text-gray-400">
                                    <Shield className="w-3 h-3 inline mr-1 text-red-500" />
                                    {admin.username}
                                </span>
                                {member && (
                                    <button
                                        onClick={backToUser}
                                        className="btn-primary text-xs py-2 px-4 whitespace-nowrap"
                                        data-testid="back-to-user-button"
                                    >
                                        <User className="w-3 h-3 inline mr-1" />
                                        Back To User
                                    </button>
                                )}
                                <button
                                    onClick={adminLogout}
                                    className="btn-secondary text-xs py-2 px-4"
                                    data-testid="logout-button"
                                >
                                    Logout
                                </button>
                            </div>
                        ) : !member && (
                            <div className="flex items-center gap-2">
                                <Link to="/member" className="btn-primary text-xs py-2 px-4" data-testid="member-portal-button">
                                    Member Portal
                                </Link>
                                <Link to="/login" className="btn-secondary text-xs py-2 px-4" data-testid="login-button">
                                    Admin
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Mobile menu button */}
                    <button
                        className="lg:hidden p-2 text-gray-400 hover:text-white"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        data-testid="mobile-menu-toggle"
                    >
                        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile Navigation */}
            {mobileMenuOpen && (
                <div className="lg:hidden bg-black/95 border-t border-white/10" data-testid="mobile-menu">
                    <div className="px-4 py-4 space-y-2">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`block py-2 px-4 font-mono text-sm uppercase tracking-wider ${
                                    location.pathname === link.path ? 'text-red-500' : 'text-gray-400'
                                }`}
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {link.label}
                            </Link>
                        ))}
                        {admin && (
                            <Link
                                to="/admin"
                                className="block py-2 px-4 font-mono text-sm uppercase tracking-wider text-gray-400"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                <Shield className="w-3 h-3 inline mr-2" />
                                Admin
                            </Link>
                        )}
                        <div className="pt-4 border-t border-white/10 space-y-2">
                            {member && !admin && (
                                <>
                                    <div className="py-2 px-4 font-mono text-xs text-green-400">
                                        <User className="w-3 h-3 inline mr-1" />
                                        {member.callsign}
                                    </div>
                                    {member.has_linked_admin && (
                                        <button
                                            onClick={() => { switchToAdmin(); setMobileMenuOpen(false); }}
                                            className="w-full btn-primary text-xs py-2"
                                            data-testid="mobile-switch-to-admin-button"
                                        >
                                            Switch To Admin
                                        </button>
                                    )}
                                    <button
                                        onClick={() => { memberLogout(); setMobileMenuOpen(false); }}
                                        className="w-full btn-secondary text-xs py-2"
                                    >
                                        Logout
                                    </button>
                                </>
                            )}
                            {admin ? (
                                <>
                                {member && (
                                    <button
                                        onClick={() => { backToUser(); setMobileMenuOpen(false); }}
                                        className="w-full btn-primary text-xs py-2"
                                        data-testid="mobile-back-to-user-button"
                                    >
                                        Back To User
                                    </button>
                                )}
                                <button
                                    onClick={() => { adminLogout(); setMobileMenuOpen(false); }}
                                    className="w-full btn-secondary text-xs py-2"
                                >
                                    Logout
                                </button>
                                </>
                            ) : !member && (
                                <>
                                    <Link
                                        to="/member"
                                        className="block text-center btn-primary text-xs py-2"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Member Portal
                                    </Link>
                                    <Link
                                        to="/login"
                                        className="block text-center btn-secondary text-xs py-2"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Admin Login
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};
